import { DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, importProvidersFrom } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';

import { ErrorHandlerService, LOGGER_CONFIG } from 'tmt-logger';
import { providePrimeNG } from 'primeng/config';
import Material from '@primeng/themes/material';
import { definePreset } from '@primeng/themes';

import { routes } from 'src/app/routes/routes';
import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { AppConfigService } from 'src/app/services/app-config.service';
import { LaunchDarklyLogger } from 'src/app/services/launchdarkly-logger.service';
import { SessionService } from 'src/app/services/session.service';
import { UtilsService } from 'src/app/services/utils.service';
import { tratonPreset } from 'src/app/primeng-design-tokens/traton-preset';

import { AppComponent } from './app/app.component';
import { LoginService } from './app/components/login/login.service';
import { AuthService } from './app/services/auth.service';
import { LaunchDarklyService } from './app/services/launchdarkly.service';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from 'src/environments/environment';

bootstrapApplication(AppComponent, {
	providers: [
		importProvidersFrom(BrowserModule, FormsModule),
		AppConfigService,
		{
			provide: APP_INITIALIZER,
			useFactory: (appConfigService: AppConfigService) => () => appConfigService.load(),
			deps: [AppConfigService],
			multi: true,
		},
		{
			provide: LOGGER_CONFIG,
			useFactory: (appConfigService: AppConfigService) => appConfigService.logConfig$,
			deps: [AppConfigService],
		},
		{
			provide: ErrorHandler,
			useClass: ErrorHandlerService,
		},
		LoginService,
		AuthService,
		SessionService,
		ApolloClientService,
		UtilsService,
		LaunchDarklyLogger,
		LaunchDarklyService,
		DatePipe,
		provideAnimations(),
		provideHttpClient(withInterceptorsFromDi()),
		provideRouter(routes),
		providePrimeNG({
			theme: {
				options: {
					darkModeSelector: '.theme-dark',
				},
				preset: definePreset(Material, tratonPreset, {
					components: {
						card: {
							body: {
								padding: '1rem',
							},
						},
						menu: {
							list: {
								padding: '0',
							},
						},
						listbox: {
							colorScheme: {
								light: {
									root: {
										background: '{surface.50}',
									},
								},
								dark: {
									root: {
										background: '{surface.1000}',
									},
								},
							},
						},
						datatable: {
							colorScheme: {
								light: {
									header: {
										background: '{surface.150}',
									},
								},
								dark: {
									headerCell: {
										color: '{surface.200}',
									},
									header: {
										background: '{surface.950}',
									},
									bodyCell: {
										borderColor: '{surface.800}',
									},
								},
							},
						},
						dialog: {
							content: {
								padding: '1.5rem 1rem 1rem 1rem ',
							},
							extend: {
								size: {
									maxWidth: '90%',
									maxHeight: '90%',
								},
							},
							css: ({ dt }) => `
							.p-dialog {
								max-width: ${dt('dialog.size.max.width')};
                max-height: ${dt('dialog.size.max.height')};
							}
							`,
						},

						button: {
							colorScheme: {
								dark: {
									root: {
										primary: {
											background: '{surface.500}',
											borderColor: '{surface.700}',
										},
									},
									text: {
										secondary: {
											color: '{surface.200}',
										},
									},
								},
							},
							root: {
								primary: {},
							},
							css: () => `
                .p-datatable-column-filter-button {
                  margin: 0;
                  width: 1rem;
                }
                `,
						},
					},
				}),
			},
		}),
		provideServiceWorker('ngsw-worker.js', {
			enabled: environment.serviceWorkerEnabled,
			// We primarily use service worker to check for updates, so register ASAP.
			// We might want to change this to "registerWhenStable" if use cases change.
			registrationStrategy: 'registerImmediately',
		}),
	],
}).catch(err => console.error(err));
